
/**
 * Utilidades para manejar roles y permisos
 */

/**
 * Mapeo de roles de usuario a roles del sistema
 */
export const userRoleToSystemRoles: Record<string, string[]> = {
  'admin': ['super_admin'],
  'manager': ['admin_general'],
  'president': ['president'],
  'vocal': ['vocal'],
  'neighbor': ['owner'],
  'renter': ['tenant'],
  // Agregar más mapeos según sea necesario
};

/**
 * Verifica si un rol tiene acceso a cierta funcionalidad
 * @param userRole El rol del usuario
 * @param requiredRole El rol requerido para la funcionalidad
 * @returns true si tiene acceso, false en caso contrario
 */
export function hasAccessByRole(userRole: string, requiredRole: string): boolean {
  // Nivel jerárquico de roles (mayor número = más permisos)
  const roleHierarchy: Record<string, number> = {
    'admin': 100,
    'manager': 90, 
    'president': 70,
    'vocal': 60,
    'neighbor': 50,
    'renter': 40,
  };
  
  // El admin o manager (admin comunidad) tienen acceso a la configuración y usuarios
  if (requiredRole === 'admin' && (userRole === 'admin' || userRole === 'manager')) return true;
  
  // Verificar si el rol del usuario tiene un nivel mayor o igual al requerido
  return (roleHierarchy[userRole] || 0) >= (roleHierarchy[requiredRole] || 0);
}

/**
 * Obtiene el nombre legible de un rol
 * @param role Código del rol
 * @returns Nombre del rol en formato legible
 */
export function getRoleName(role: string): string {
  const roleNames: Record<string, string> = {
    'admin': 'Administrador de App',
    'manager': 'Administrador de Comunidad',
    'president': 'Presidente',
    'vocal': 'Vocal',
    'neighbor': 'Vecino',
    'renter': 'Inquilino',
    'admin_general': 'Administrador de Comunidad',
    'owner': 'Vecino',
    'tenant': 'Inquilino',
  };
  
  return roleNames[role] || role;
}

/**
 * Indica si un rol debe considerarse con privilegios de administración en la UI
 * (agrupa admin + manager según la lógica de hasAccessByRole)
 */
export function isAdminLike(role?: string | null): boolean {
  if (!role) return false;
  return hasAccessByRole(role, 'admin');
}

/**
 * Indica si el rol tiene permisos para programar una junta
 */
export function canScheduleMeeting(role?: string | null): boolean {
  if (!role) return false;
  return role === 'admin' || role === 'manager' || role === 'president';
}

/**
 * Indica si un rol es considerado propietario / vecino con propiedad en la comunidad.
 * Los roles 'president' y 'vocal' son siempre considerados propietarios al igual que 'neighbor'.
 */
export function isOwnerRole(role?: string | null): boolean {
  if (!role) return false;
  return role === 'neighbor' || role === 'president' || role === 'vocal';
}

